import { fetchIsWarehouseAdmin } from "@/auth/api/invite-api";
import {
  accessLevelFromAdmin,
  canAccessRoute,
  defaultRouteForLevel,
  isAccessLoading,
  type AccessLevel,
  type RouteKey,
} from "@/auth/permissions";
import { useAuth } from "@/auth/AuthProvider";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface AccessContextValue {
  accessLevel: AccessLevel;
  loading: boolean;
  isAdmin: boolean;
  canAccess: (route: RouteKey) => boolean;
  defaultRoute: string;
}

const AccessContext = createContext<AccessContextValue | null>(null);

export function AccessProvider({ children }: { children: ReactNode }) {
  const { session, loading: authLoading } = useAuth();
  const userId = session?.user.id ?? null;
  // Which user the admin lookup below resolved for. Compared against the live userId
  // during render so a session arriving this commit reads as loading, not as "normal".
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!userId) {
      setIsAdmin(false);
      setResolvedUserId(null);
      return;
    }

    let mounted = true;
    fetchIsWarehouseAdmin()
      .then((value) => {
        if (mounted) setIsAdmin(value);
      })
      .catch(() => {
        if (mounted) setIsAdmin(false);
      })
      .finally(() => {
        if (mounted) setResolvedUserId(userId);
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  const accessLevel = accessLevelFromAdmin(isAdmin);
  const loading = isAccessLoading({ authLoading, userId, resolvedUserId });

  const value = useMemo<AccessContextValue>(
    () => ({
      accessLevel,
      loading,
      isAdmin,
      canAccess: (route: RouteKey) => canAccessRoute(accessLevel, route),
      defaultRoute: defaultRouteForLevel(accessLevel),
    }),
    [accessLevel, loading, isAdmin],
  );

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): AccessContextValue {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("useAccess must be used within AccessProvider");
  }
  return context;
}
